import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { notification } from "antd"
import { getSession, postLogin, postLogout, TSession } from "../../auth/sessionApi"
import { resetSessionExpired, setNoCustomerHandler, setUnauthorizedHandler, TUnauthorizedReason } from "../../auth/AuthenticatedAxios"
import { loginPathFor } from "../../auth/redirect"
import { messageInfo } from "../../service/messages"
import { ModalContext } from "./ModalContext"

export type AuthContextProps = {
    isAuthenticated: boolean
    isLoading: boolean
    /** angemeldet, aber kein Portal-Zugangsdatensatz (403 "No customer found for this login") */
    noCustomer: boolean
    session?: TSession
    login: (email: string, password: string) => Promise<void>
    logout: (reason?: TUnauthorizedReason) => Promise<void>
}

export const AuthContext = createContext<AuthContextProps | undefined>(undefined)

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (!context) throw new Error("useAuth must be used within AuthContextProvider")
    return context
}

export const AuthContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [session, setSession] = useState<TSession | undefined>(undefined)
    const [isLoading, setIsLoading] = useState(true)
    const [noCustomer, setNoCustomer] = useState(false)
    const isLoggingOutRef = useRef(false)
    const sessionRef = useRef<TSession | undefined>(undefined)
    sessionRef.current = session
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const modalContext = useContext(ModalContext)
    // Kontext über eine Ref, damit logout stabil bleibt (der Kontextwert ändert sich bei jedem Öffnen/Schließen)
    const modalRef = useRef(modalContext)
    modalRef.current = modalContext

    // beim Start: bestehende Portal-Session prüfen (200 -> angemeldet, 401 -> Login-Seite); ein Zentrale-Cookie zählt hier nicht
    useEffect(() => {
        let cancelled = false
        getSession()
            .then((data) => !cancelled && setSession(data))
            .catch(() => !cancelled && setSession(undefined))
            .finally(() => !cancelled && setIsLoading(false))
        return () => {
            cancelled = true
        }
    }, [])

    const login = useCallback(
        async (email: string, password: string) => {
            const data = await postLogin(email, password)
            // keine Daten eines vorherigen Benutzers weiterverwenden
            queryClient.clear()
            resetSessionExpired()
            setNoCustomer(false)
            setSession(data)
        },
        [queryClient]
    )

    const logout = useCallback(
        async (reason?: TUnauthorizedReason) => {
            // schon abgemeldet (z. B. verspätete 401 einer alten Anfrage) -> nichts tun
            if (isLoggingOutRef.current || !sessionRef.current) return
            isLoggingOutRef.current = true
            try {
                // Aufräumen bei jedem Logout (manuell und Sitzungsablauf)
                modalRef.current?.closeAll()
                notification.destroy()
                if (reason === "session-expired") {
                    messageInfo("session-expired", "Sitzung abgelaufen", "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.", 6)
                    // zurück auf die aktuelle Seite nach erneuter Anmeldung
                    navigate(loginPathFor(window.location.pathname, window.location.search, window.location.hash), { replace: true })
                } else {
                    navigate("/login", { replace: true })
                }
                setSession(undefined)
                setNoCustomer(false)
                queryClient.clear()
                if (reason !== "session-expired") {
                    // Portal-Cookie serverseitig löschen; Fehler ignorieren (Session evtl. schon ungültig)
                    await postLogout().catch(() => undefined)
                }
            } finally {
                isLoggingOutRef.current = false
            }
        },
        [navigate, queryClient]
    )

    useEffect(() => {
        setUnauthorizedHandler((reason) => void logout(reason))
        setNoCustomerHandler(() => sessionRef.current && setNoCustomer(true))
        return () => {
            setUnauthorizedHandler(null)
            setNoCustomerHandler(null)
        }
    }, [logout])

    const value = useMemo(
        () => ({ isAuthenticated: !!session, isLoading, noCustomer, session, login, logout }),
        [session, isLoading, noCustomer, login, logout]
    )

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
